import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Table, Spinner, Alert, Form, InputGroup, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const formatBoolean = (value, yes = 'Yes', no = 'No') => (value === null || value === undefined ? '' : value ? yes : no);
const formatDateTime = (value) => (value ? apiService.formatDateTime(value) : '');
const formatOrganization = (row) => (row.organization_code ? `${row.organization_name} (${row.organization_code})` : row.organization_name);

const SUCCESSFUL_COLUMNS = [
  { key: 'impersonation_session_clearance_id', label: 'Clearance ID' },
  { key: 'organization_name', label: 'Organization', render: formatOrganization },
  { key: 'enterprise_user_name', label: 'Enterprise User' },
  { key: 'enterprise_user_email', label: 'Enterprise Email' },
  { key: 'platform_user_name', label: 'Platform User' },
  { key: 'platform_user_username', label: 'Platform Username' },
  { key: 'justification', label: 'Justification' },
  { key: 'ip_address', label: 'IP Address' },
  { key: 'impersonation_attempt_timestamp', label: 'Timestamp', render: (row) => formatDateTime(row.impersonation_attempt_timestamp) },
  { key: 'session_duration_minutes', label: 'Session Duration (min)' },
  { key: 'session_active', label: 'Session Active', render: (row) => formatBoolean(row.session_active, 'Active', 'Inactive') },
  { key: 'app_version', label: 'App Version' },
  { key: 'build_number', label: 'Build Number' },
];

const UNSUCCESSFUL_COLUMNS = [
  { key: 'impersonation_session_attempt_id', label: 'Attempt ID' },
  { key: 'identifier_attempted', label: 'Identifier Attempted' },
  { key: 'impersonation_attempt_timestamp', label: 'Timestamp', render: (row) => formatDateTime(row.impersonation_attempt_timestamp) },
  { key: 'failure_reason', label: 'Failure Reason' },
  { key: 'platform_login_attempt_result', label: 'Platform Login Result' },
  { key: 'platform_login_failure_reason', label: 'Platform Login Failure Reason' },
  { key: 'organization_name', label: 'Organization', render: formatOrganization },
  { key: 'enterprise_user_name', label: 'Enterprise User' },
  { key: 'enterprise_user_email', label: 'Enterprise Email' },
  { key: 'platform_user_name', label: 'Platform User' },
  { key: 'justification', label: 'Justification' },
  { key: 'ip_address', label: 'IP Address' },
];

const renderPagination = (pagination, currentPage, onPageChange) => {
  if (pagination.total_pages <= 1) return null;
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(pagination.total_pages, start + maxVisible - 1);
  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return (
    <Pagination className="mb-0">
      <Pagination.First disabled={currentPage === 1} onClick={() => onPageChange(1)} />
      <Pagination.Prev disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
      {start > 1 && <Pagination.Ellipsis disabled />}
      {pages.map(p => (
        <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
          {p}
        </Pagination.Item>
      ))}
      {end < pagination.total_pages && <Pagination.Ellipsis disabled />}
      <Pagination.Next disabled={currentPage === pagination.total_pages} onClick={() => onPageChange(currentPage + 1)} />
      <Pagination.Last disabled={currentPage === pagination.total_pages} onClick={() => onPageChange(pagination.total_pages)} />
    </Pagination>
  );
};

const ImpersonationSection = ({ title, headerVariant, endpoint, columns, rowKey, noun }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState({ column: null, direction: 'asc' });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [pagination, setPagination] = useState({ total_records: 0, page: 1, limit: 50, total_pages: 1 });

  const fetchRows = useCallback((page, limit, search) => {
    setLoading(true);
    setError(null);
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    apiService.get(`${endpoint}?${queryParams}`)
      .then((response) => {
        setRows(response.data || response);
        if (response.pagination) {
          setPagination(response.pagination);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || `Failed to fetch ${noun}`);
        setLoading(false);
      });
  }, [endpoint, noun]);

  useEffect(() => {
    fetchRows(page, limit, searchTerm);
  }, [page, limit, searchTerm, fetchRows]);

  let sortedRows = Array.isArray(rows) ? [...rows] : [];
  if (sort.column) {
    sortedRows.sort((a, b) => {
      let aVal = a[sort.column];
      let bVal = b[sort.column];
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  const handleSort = (column) => {
    setSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
  };

  const applySearch = (value) => {
    setPage(1);
    setSearchTerm(value);
  };

  return (
    <Card>
      <Card.Header className={`bg-${headerVariant} text-white d-flex justify-content-between align-items-center`}>
        <span>{title}</span>
        <div className="d-flex align-items-center">
          <Form.Label className="text-white mb-0 me-2" style={{ fontSize: '0.85rem' }}>Per page:</Form.Label>
          <Form.Select
            size="sm"
            style={{ width: 'auto' }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Form.Select>
        </div>
      </Card.Header>
      <Card.Body>
        <InputGroup className="mb-3">
          <InputGroup.Text>
            <i className="bi bi-search"></i>
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder={`Search ${noun}...`}
            onKeyDown={(e) => {
              if (e.key === 'Enter') applySearch(e.target.value);
            }}
            onBlur={(e) => applySearch(e.target.value)}
          />
        </InputGroup>
        {loading ? (
          <div className="text-center py-4"><Spinner animation="border" /> Loading...</div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <div style={{ maxHeight: '33vh', overflowY: 'auto' }}>
            <Table striped bordered hover responsive size="sm">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th key={col.key} style={{ cursor: 'pointer' }} onClick={() => handleSort(col.key)}>
                      {col.label} {sort.column === col.key ? (sort.direction === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.length > 0 ? (
                  sortedRows.map((row) => (
                    <tr key={row[rowKey]}>
                      {columns.map(col => (
                        <td key={col.key}>{col.render ? col.render(row) : row[col.key]}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="text-center">
                      {searchTerm ? `No matching ${noun} found.` : `No ${noun} found.`}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
        {pagination.total_pages > 1 && (
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div className="text-muted" style={{ fontSize: '0.85rem' }}>
              Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total_records)} of {pagination.total_records} entries
            </div>
            {renderPagination(pagination, page, setPage)}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const Impersonations = () => {
  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-person-badge me-2"></i>
              Impersonations
            </h2>
          </div>
        </Col>
      </Row>

      {/* Successful Impersonations Section */}
      <Row className="mb-5">
        <Col>
          <ImpersonationSection
            title="Successful Impersonations"
            headerVariant="success"
            endpoint="/impersonations_successful"
            columns={SUCCESSFUL_COLUMNS}
            rowKey="impersonation_session_clearance_id"
            noun="successful impersonations"
          />
        </Col>
      </Row>

      {/* Unsuccessful Impersonations Section */}
      <Row>
        <Col>
          <ImpersonationSection
            title="Unsuccessful Impersonation Attempts"
            headerVariant="danger"
            endpoint="/impersonations_unsuccessful"
            columns={UNSUCCESSFUL_COLUMNS}
            rowKey="impersonation_session_attempt_id"
            noun="unsuccessful impersonations"
          />
        </Col>
      </Row>
    </div>
  );
};

export default Impersonations;
